import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { gardenAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Flower2,
  Droplets,
  Sun,
  Award,
  TreeDeciduous,
  Sprout,
  Leaf,
  Heart,
  AlertTriangle,
  Trophy,
  Calendar,
  Sparkles,
  RefreshCw,
  ChevronRight,
  Lock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GardenQuestionnaire } from '@/components/garden/GardenQuestionnaire';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';

export function GardenPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.is_premium || user?.is_founder;
  const [loading, setLoading] = useState(true);
  const [questionnaireStatus, setQuestionnaireStatus] = useState(null);
  const [myPlants, setMyPlants] = useState([]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [catalog, setCatalog] = useState(null);
  
  // Modal states
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showPlantSelect, setShowPlantSelect] = useState(false);
  const [showRelapseConfirm, setShowRelapseConfirm] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [relapsingPlant, setRelapsingPlant] = useState(null);
  const [customName, setCustomName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadGardenData();
  }, []);

  const loadGardenData = async () => {
    setLoading(true);
    try {
      const [statusRes, plantsRes, statsRes, badgesRes, catalogRes] = await Promise.all([
        gardenAPI.getQuestionnaireStatus(),
        gardenAPI.getMyPlants(),
        gardenAPI.getStats(),
        gardenAPI.getBadges(),
        gardenAPI.getPlantsCatalog()
      ]);
      
      setQuestionnaireStatus(statusRes.data);
      setMyPlants(plantsRes.data.plants || []);
      setStats(statsRes.data);
      setBadges(badgesRes.data.badges || []);
      setCatalog(catalogRes.data);
      
      // Se não completou questionário e não tem plantas, mostrar questionário
      if (!statusRes.data.completed && plantsRes.data.total === 0) {
        setShowQuestionnaire(true);
      }
    } catch (error) {
      console.error('Failed to load garden data:', error);
      toast.error('Erro ao carregar dados do jardim');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaireComplete = async (result) => {
    setShowQuestionnaire(false);
    setQuestionnaireStatus({ completed: true, result });
    await loadGardenData();
    
    // Mostrar modal para selecionar planta
    setShowPlantSelect(true);
  };

  const handleWaterPlant = async (plantId) => {
    setActionLoading(true);
    try {
      const response = await gardenAPI.waterPlant(plantId);
      toast.success(response.data.message);
      await loadGardenData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao regar planta';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlantSeed = async () => {
    if (!selectedPlant) {
      toast.error('Selecione uma planta');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await gardenAPI.plantSeed(selectedPlant.id, customName || null);
      toast.success(response.data.message);
      setShowPlantSelect(false);
      setSelectedPlant(null);
      setCustomName('');
      await loadGardenData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao plantar semente';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReportRelapse = async () => {
    if (!relapsingPlant) return;
    
    setActionLoading(true);
    try {
      const response = await gardenAPI.reportRelapse(relapsingPlant.id);
      toast.info(response.data.message);
      setShowRelapseConfirm(false);
      setRelapsingPlant(null);
      await loadGardenData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao reportar recaída';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'seed': return <Sprout className="h-6 w-6 text-green-600" />;
      case 'growing': return <Leaf className="h-6 w-6 text-green-500" />;
      case 'mature': return <Flower2 className="h-6 w-6 text-pink-500" />;
      default: return <Sprout className="h-6 w-6" />;
    }
  };

  const getStageName = (stage) => {
    switch (stage) {
      case 'seed': return 'Semente/Broto';
      case 'growing': return 'Crescimento';
      case 'mature': return 'Adulta/Floração';
      default: return stage;
    }
  };

  const getHealthBadge = (health) => {
    switch (health) {
      case 'healthy':
        return <Badge className="bg-green-100 text-green-700">Saudável</Badge>;
      case 'wilted':
        return <Badge className="bg-yellow-100 text-yellow-700">Murcha</Badge>;
      case 'relapsed':
        return <Badge className="bg-orange-100 text-orange-700">Renascendo</Badge>;
      default:
        return <Badge variant="outline">{health}</Badge>;
    }
  };

  const getCategoryInfo = (category) => {
    switch (category) {
      case 'annual':
        return { name: 'Anual', color: 'text-amber-600', bg: 'bg-amber-100' };
      case 'biennial':
        return { name: 'Bienal', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'perennial':
        return { name: 'Perene', color: 'text-emerald-600', bg: 'bg-emerald-100' };
      default:
        return { name: category, color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const canWaterToday = (plant) => {
    if (!plant.last_watered) return true;
    const lastWatered = new Date(plant.last_watered);
    const today = new Date();
    return lastWatered.toDateString() !== today.toDateString();
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6" data-testid="garden-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="max-w-5xl mx-auto space-y-6" data-testid="garden-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <Badge variant="outline" className="text-[10px] rounded-md px-1.5 py-0 h-5 border-green-300 text-green-700 dark:text-green-400 dark:border-green-700">Cuidado Diário</Badge>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Flower2 className="h-6 w-6 text-green-600" strokeWidth={1.5} />
            Planta do Dia
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cuide da sua planta diariamente — ela evolui com você
          </p>
        </div>
        
        <div className="flex gap-2">
          {questionnaireStatus?.completed && myPlants.length === 0 && (
            <Button onClick={() => setShowPlantSelect(true)} className="rounded-xl" data-testid="plant-seed-btn">
              <Sprout className="mr-2 h-4 w-4" />
              Plantar Semente
            </Button>
          )}
          {!questionnaireStatus?.completed && (
            <Button onClick={() => setShowQuestionnaire(true)} className="rounded-xl" data-testid="start-questionnaire-btn">
              <Sparkles className="mr-2 h-4 w-4" />
              Descobrir Minha Planta
            </Button>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <TreeDeciduous className="h-5 w-5 text-green-600 dark:text-green-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Plantas Ativas</p>
                  <p className="text-xl font-bold">{stats.active_plants}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Droplets className="h-5 w-5 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sequência</p>
                  <p className="text-xl font-bold">{stats.current_streak} dias</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                  <Sun className="h-5 w-5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">XP Total</p>
                  <p className="text-xl font-bold">{stats.total_xp}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                  <Award className="h-5 w-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nível da Raiz</p>
                  <p className="text-xl font-bold">{stats.root_level_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cross-nav to Árvore Viva NFT */}
      <Card className="rounded-2xl border-border/40 shadow-sm bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border-emerald-200 dark:border-emerald-800/30 cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/minha-arvore')} data-testid="nft-crossnav-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
              <TreeDeciduous className="h-6 w-6 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-emerald-900 dark:text-emerald-200">Árvore Viva NFT</h3>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">Evolua sua árvore e colecione conquistas da sua jornada</p>
            </div>
            <ChevronRight className="h-5 w-5 text-emerald-400 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="plants" className="space-y-4">
        <TabsList className="rounded-xl">
          <TabsTrigger value="plants" className="rounded-lg gap-1.5" data-testid="tab-plants">
            <Flower2 className="h-4 w-4" />
            Minhas Plantas
          </TabsTrigger>
          <TabsTrigger value="catalog" className="rounded-lg gap-1.5" data-testid="tab-catalog">
            <TreeDeciduous className="h-4 w-4" />
            Catálogo
          </TabsTrigger>
          <TabsTrigger value="badges" className="rounded-lg gap-1.5" data-testid="tab-badges">
            <Trophy className="h-4 w-4" />
            Insígnias
          </TabsTrigger>
        </TabsList>

        {/* My Plants Tab */}
        <TabsContent value="plants" className="space-y-4">
          {myPlants.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Sprout className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Seu jardim está vazio</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Responda ao questionário para descobrir qual planta combina com você
                </p>
                {!questionnaireStatus?.completed ? (
                  <Button onClick={() => setShowQuestionnaire(true)}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Começar Questionário
                  </Button>
                ) : (
                  <Button onClick={() => setShowPlantSelect(true)}>
                    <Sprout className="mr-2 h-4 w-4" />
                    Escolher Planta
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {myPlants.map((plant, idx) => {
                const catalogInfo = plant.catalog_info || {};
                const canWater = canWaterToday(plant);
                const xpProgress = (plant.xp_total / (plant.stage === 'seed' ? 100 : 500)) * 100;
                
                return (
                  <FadeInItem key={plant.id} index={idx}>
                  <Card className="overflow-hidden rounded-2xl border-border/40 shadow-sm" data-testid={`plant-card-${plant.id}`}>
                    {/* Plant Image */}
                    <div className="h-36 bg-gradient-to-br from-green-100 to-emerald-200 dark:from-green-900/30 dark:to-emerald-900/20 flex items-center justify-center relative">
                      <div className="text-6xl">
                        {plant.stage === 'seed' && '🌱'}
                        {plant.stage === 'growing' && '🌿'}
                        {plant.stage === 'mature' && '🌸'}
                      </div>
                      {plant.is_primary && (
                        <Badge className="absolute top-2 right-2 bg-amber-500">Principal</Badge>
                      )}
                      {plant.health === 'wilted' && (
                        <div className="absolute inset-0 bg-yellow-500/20 flex items-center justify-center">
                          <Badge className="bg-yellow-600 text-white">Precisa de água!</Badge>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">
                            {plant.name_custom || catalogInfo.name || 'Planta'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {catalogInfo.name_scientific}
                          </p>
                        </div>
                        {getHealthBadge(plant.health)}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        {getStageIcon(plant.stage)}
                        <span>{getStageName(plant.stage)}</span>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Progresso</span>
                          <span>{plant.xp_total} XP</span>
                        </div>
                        <Progress value={Math.min(xpProgress, 100)} className="h-2" />
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Droplets className="h-4 w-4" />
                          <span>{plant.days_watered} dias</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{plant.consecutive_days} sequência</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button 
                          onClick={() => handleWaterPlant(plant.id)}
                          disabled={!canWater || actionLoading}
                          className="flex-1"
                          variant={canWater ? "default" : "secondary"}
                          data-testid={`water-plant-${plant.id}`}
                        >
                          <Droplets className="mr-2 h-4 w-4" />
                          {canWater ? 'Regar' : 'Já regou hoje'}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setRelapsingPlant(plant);
                            setShowRelapseConfirm(true);
                          }}
                          title="Reportar recaída"
                          data-testid={`relapse-btn-${plant.id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  </FadeInItem>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog" className="space-y-6">
          {catalog && ['annual', 'biennial', 'perennial'].map(category => {
            const categoryInfo = getCategoryInfo(category);
            const plants = catalog.by_category[category] || [];
            
            return (
              <div key={category}>
                <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${categoryInfo.color}`}>
                  <Badge className={categoryInfo.bg}>{categoryInfo.name}</Badge>
                  <span className="text-muted-foreground text-sm font-normal">
                    ({plants.length} plantas)
                  </span>
                </h3>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  {plants.map(plant => (
                    <Card key={plant.id} className="hover:shadow-md transition-shadow cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="text-3xl">
                            {category === 'annual' && '🌻'}
                            {category === 'biennial' && '🌾'}
                            {category === 'perennial' && '🌳'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{plant.name}</h4>
                            <p className="text-xs text-muted-foreground italic truncate">
                              {plant.name_scientific}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {plant.meaning}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs">
                              <Badge variant="outline" className="text-xs">
                                +{plant.xp_per_water} XP/dia
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {badges.map(badge => (
              <Card 
                key={badge.id} 
                className={badge.unlocked ? '' : 'opacity-60'}
                data-testid={`badge-${badge.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`p-3 rounded-full ${badge.unlocked ? 'bg-amber-100' : 'bg-gray-100'}`}>
                    {badge.unlocked ? (
                      <Trophy className="h-6 w-6 text-amber-600" />
                    ) : (
                      <Lock className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium">{badge.name}</h4>
                    <p className="text-sm text-muted-foreground">{badge.description}</p>
                  </div>
                  {badge.unlocked && (
                    <Badge className="ml-auto bg-green-100 text-green-700">
                      Conquistada
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Questionnaire Modal */}
      <GardenQuestionnaire
        open={showQuestionnaire}
        onClose={() => setShowQuestionnaire(false)}
        onComplete={handleQuestionnaireComplete}
      />

      {/* Plant Selection Modal */}
      <Dialog open={showPlantSelect} onOpenChange={setShowPlantSelect}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sprout className="h-5 w-5 text-green-600" />
              Escolha Sua Planta
            </DialogTitle>
            <DialogDescription>
              {questionnaireStatus?.result?.recommended_plants?.length > 0 
                ? 'Baseado no seu perfil, recomendamos estas plantas. Mas você pode escolher qualquer uma!'
                : 'Escolha a planta que mais combina com sua jornada de recuperação'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recommended Section */}
            {questionnaireStatus?.result?.profile_type && (
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">
                  Seu perfil: {questionnaireStatus.result.profile_type === 'intensity' && 'Ciclo Intenso (Anuais)'}
                  {questionnaireStatus.result.profile_type === 'transition' && 'Fase de Transição (Bienais)'}
                  {questionnaireStatus.result.profile_type === 'resilience' && 'Resiliência Profunda (Perenes)'}
                </p>
              </div>
            )}
            
            {/* All Plants */}
            {catalog && ['annual', 'biennial', 'perennial'].map(category => {
              const categoryInfo = getCategoryInfo(category);
              const plants = catalog.by_category[category] || [];
              
              return (
                <div key={category}>
                  <h4 className={`font-medium mb-2 ${categoryInfo.color}`}>
                    Plantas {categoryInfo.name}s
                  </h4>
                  <div className="grid gap-2 grid-cols-2 md:grid-cols-4">
                    {plants.map(plant => (
                      <button
                        key={plant.id}
                        onClick={() => setSelectedPlant(plant)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          selectedPlant?.id === plant.id 
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-500' 
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                        }`}
                        data-testid={`select-plant-${plant.id}`}
                      >
                        <div className="text-2xl mb-1">
                          {category === 'annual' && '🌻'}
                          {category === 'biennial' && '🌾'}
                          {category === 'perennial' && '🌳'}
                        </div>
                        <div className="font-medium text-sm truncate">{plant.name}</div>
                        <div className="text-xs text-muted-foreground">+{plant.xp_per_water} XP</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Custom Name */}
            {selectedPlant && (
              <div className="pt-4 border-t">
                <Label htmlFor="customName">Nome personalizado (opcional)</Label>
                <Input
                  id="customName"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder={`Ex: Minha ${selectedPlant.name}`}
                  className="mt-1"
                  data-testid="plant-custom-name"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlantSelect(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handlePlantSeed}
              disabled={!selectedPlant || actionLoading}
              data-testid="confirm-plant-btn"
            >
              {actionLoading ? 'Plantando...' : 'Plantar Semente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relapse Confirmation Modal */}
      <Dialog open={showRelapseConfirm} onOpenChange={setShowRelapseConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Reportar Recaída
            </DialogTitle>
            <DialogDescription>
              <p className="mb-4">
                Entendemos que a recuperação tem altos e baixos. Reportar uma recaída irá:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Sua planta perderá as flores, mas <strong>a raiz permanece</strong> (50% do XP preservado)</li>
                <li>O estágio voltará, mas você não perde tudo</li>
                <li>A sequência de dias será reiniciada</li>
              </ul>
              <p className="mt-4 text-sm text-muted-foreground italic">
                "A recuperação é um processo, não um destino. Continue regando."
              </p>
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRelapseConfirm(false)}>
              Voltar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReportRelapse}
              disabled={actionLoading}
              data-testid="confirm-relapse-btn"
            >
              {actionLoading ? 'Processando...' : 'Confirmar Recaída'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageTransition>
  );
}
